import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Camera, Package, X, RotateCcw, Grid3X3 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useIsMobile } from '@/hooks/use-mobile';

interface GoodsPhotoGridProps {
  photos: string[];
  onPhotosChange: (photos: string[]) => void;
}

export const GoodsPhotoGrid = ({ photos, onPhotosChange }: GoodsPhotoGridProps) => {
  const [isUploading, setIsUploading] = useState(false);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const isMobile = useIsMobile();

  const triggerCamera = () => {
    fileInputRef.current?.click();
  };

  const handlePhotoCapture = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    
    try {
      // Upload to product-images bucket (we'll create this or use existing)
      const fileName = `goods-${Date.now()}-${file.name}`;
      const { data, error } = await supabase.storage
        .from('product-images')
        .upload(fileName, file);

      if (error) {
        throw new Error(`Upload failed: ${error.message}`);
      }

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('product-images')
        .getPublicUrl(fileName);

      const newPhotos = [...photos, urlData.publicUrl];
      onPhotosChange(newPhotos);
      
      toast({
        title: "Product Photo Captured",
        description: `Photo ${newPhotos.length} added successfully`,
      });
    } catch (error: any) {
      console.error('Photo upload failed:', error);
      toast({
        title: "Upload Failed",
        description: error.message || "Failed to upload photo",
        variant: "destructive"
      });
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const removePhoto = (index: number) => {
    const newPhotos = photos.filter((_, i) => i !== index);
    onPhotosChange(newPhotos);
    setSelectedPhotoIndex(null);
    
    toast({
      title: "Photo Removed",
      description: "Product photo has been removed",
    });
  };

  const retakePhoto = (index: number) => {
    setSelectedPhotoIndex(index);
    triggerCamera();
  };

  const replacePhoto = async (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const file = event.target.files?.[0];
    if (!file || selectedPhotoIndex === null) return;

    setIsUploading(true);
    
    try {
      // Upload replacement photo
      const fileName = `goods-${Date.now()}-${file.name}`;
      const { data, error } = await supabase.storage
        .from('product-images')
        .upload(fileName, file);

      if (error) {
        throw new Error(`Upload failed: ${error.message}`);
      }

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('product-images')
        .getPublicUrl(fileName);

      const newPhotos = [...photos];
      newPhotos[index] = urlData.publicUrl;
      onPhotosChange(newPhotos);
      setSelectedPhotoIndex(null);
      
      toast({
        title: "Photo Replaced",
        description: `Photo ${index + 1} has been updated`,
      });
    } catch (error: any) {
      console.error('Photo replacement failed:', error);
      toast({
        title: "Upload Failed",
        description: error.message || "Failed to replace photo",
        variant: "destructive"
      });
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Suggested photo types for goods
  const photoSuggestions = [
    "Overall delivery view",
    "Product packaging condition", 
    "Quality close-ups",
    "Damage or issues (if any)",
    "Product labels/expiry dates"
  ];

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <Package className="w-12 h-12 mx-auto text-primary" />
        <h2 className="text-2xl font-semibold">Goods Photos</h2>
        <p className="text-muted-foreground">
          Capture photos of the delivered products for quality documentation
        </p>
        {photos.length > 0 && (
          <Badge variant="secondary" className="text-sm">
            {photos.length} photo{photos.length !== 1 ? 's' : ''} captured
          </Badge>
        )}
      </div>

      {/* Photo Grid */}
      {photos.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Grid3X3 className="w-4 h-4" />
            <h3 className="font-medium">Product Photos</h3>
          </div>
          <div className={`grid gap-4 ${
            isMobile ? 'grid-cols-2' : 'grid-cols-3 lg:grid-cols-4'
          }`}>
            {photos.map((photo, index) => (
              <Card key={index} className="relative overflow-hidden group">
                <CardContent className="p-0">
                  <div className="relative aspect-square">
                    <img
                      src={photo}
                      alt={`Product photo ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                    <div className="absolute top-2 left-2">
                      <Badge variant="secondary" className="text-xs">
                        {index + 1}
                      </Badge>
                    </div>
                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => retakePhoto(index)}
                        className="h-7 w-7 p-0"
                        disabled={isUploading}
                      >
                        <RotateCcw className="w-3 h-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => removePhoto(index)}
                        className="h-7 w-7 p-0"
                        disabled={isUploading}
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Camera Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={selectedPhotoIndex !== null ? 
          (e) => replacePhoto(e, selectedPhotoIndex) : 
          handlePhotoCapture
        }
        className="hidden"
        disabled={isUploading}
      />

      {/* Action Buttons */}
      <div className="space-y-3">
        <Button
          onClick={triggerCamera}
          disabled={isUploading}
          className="w-full h-14 text-lg"
          variant={photos.length === 0 ? "default" : "outline"}
        >
          {isUploading ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3" />
              Uploading...
            </>
          ) : (
            <>
              <Camera className="w-5 h-5 mr-3" />
              {photos.length === 0 ? 'Take First Photo' : 'Add Another Photo'}
            </>
          )}
        </Button>

        {/* Photo Suggestions */}
        <Card className="bg-muted/50">
          <CardContent className="p-4">
            <h4 className="font-medium mb-3">📸 Suggested Photos</h4>
            <div className="space-y-2">
              {photoSuggestions.map((suggestion, index) => (
                <div key={index} className="flex items-center gap-2 text-sm">
                  <div className="w-2 h-2 rounded-full bg-primary/60 flex-shrink-0" />
                  <span className="text-muted-foreground">{suggestion}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Quality Tips */}
        <Card className="bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800">
          <CardContent className="p-4">
            <h4 className="font-medium mb-2">💡 Photo Quality Tips</h4>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• Good lighting helps capture clear product details</li>
              <li>• Include both wide shots and close-ups of products</li>
              <li>• Document any damage or quality issues clearly</li>
              <li>• Multiple angles provide better quality evidence</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};